import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { DbService } from './db.service';
import { DataSynchronizerService } from './data-synchronization.service';
import { AppDb, DbNotification, NotificationPriority, NotificationType } from 'fakturnia-shared';
import { NotificationsApiService } from './api/notifications.api.service';

/**
 * Serwis do zarządzania powiadomieniami
 * - Dane przechowywane i odczytywane z Dexie (IndexedDB)
 * - Synchronizacja przez standardowy mechanizm synchronizacji
 * - Operacje CRUD przez API (ale dane z lokalnej bazy)
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationsService implements OnDestroy {

    private _subscriptions: Subscription[] = [];
    private _db!: AppDb;

    // === State ===
    
    /** Wszystkie powiadomienia (z lokalnej bazy) */
    private _notifications = new BehaviorSubject<DbNotification[]>([]);
    notifications$ = this._notifications.asObservable();

    /** Liczba nieprzeczytanych powiadomień */
    private _unreadCount = new BehaviorSubject<number>(0);
    unreadCount$ = this._unreadCount.asObservable();

    /** Czy ładowanie w toku */
    private _isLoading = new BehaviorSubject<boolean>(false);
    isLoading$ = this._isLoading.asObservable();

    constructor(
        private _dbService: DbService,
        private _dataSynchronizerService: DataSynchronizerService,
        private _notificationsApi: NotificationsApiService
    ) {
        // Subskrybuj bazę danych
        this._subscriptions.push(
            this._dbService.getDatabase().subscribe({
                next: (db) => {
                    this._db = db;
                    // Reset cache za każdym razem gdy baza się zmienia (logout/switch konta).
                    this._notifications.next([]);
                    this._unreadCount.next(0);
                    if (db) {
                        this.loadFromLocal();
                    }
                }
            })
        );

        // Subskrybuj synchronizację - dane przychodzą z głównego mechanizmu sync
        this._subscriptions.push(
            this._dataSynchronizerService.currentData.subscribe({
                next: (data) => {
                    if (!data || data.table !== 'notifications') return;

                    // Update lokalnych danych w pamięci
                    const currentNotifications = [...this._notifications.value];
                    
                    for (const notification of data.value as DbNotification[]) {
                        const existingIndex = currentNotifications.findIndex(n => n._id === notification._id);
                        
                        if (notification.isDeleted) {
                            // Usuń jeśli deleted
                            if (existingIndex >= 0) {
                                currentNotifications.splice(existingIndex, 1);
                            }
                        } else if (existingIndex >= 0) {
                            // Update istniejącego
                            currentNotifications[existingIndex] = notification;
                        } else {
                            // Dodaj nowe na początek
                            currentNotifications.unshift(notification);
                        }
                    }

                    // Sortuj po dacie (najnowsze na górze)
                    currentNotifications.sort((a, b) => 
                        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    );

                    this._notifications.next(currentNotifications);
                    this.updateUnreadCount();

                    this._dataSynchronizerService.received();
                }
            })
        );
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Czyści cache w pamięci (np. przy wylogowaniu / przełączeniu konta).
     */
    public clear(): void {
        this._notifications.next([]);
        this._unreadCount.next(0);
    }

    // === Public API ===

    /**
     * Ładuje powiadomienia z lokalnej bazy Dexie
     */
    async loadFromLocal(): Promise<void> {
        if (!this._db) return;

        this._isLoading.next(true);

        try {
            // Pobierz wszystkie nieusunięte powiadomienia
            const notifications = await this._db.notifications
                .filter(n => !n.isDeleted)
                .toArray();

            // Sortuj malejąco po dacie
            notifications.sort((a, b) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            this._notifications.next(notifications);
            this.updateUnreadCount();
        } catch (error) {
            console.error('[NotificationsService] Error loading from local:', error);
        } finally {
            this._isLoading.next(false);
        }
    }

    /**
     * Oznacza powiadomienie jako przeczytane
     */
    async markAsRead(notificationId: string): Promise<void> {
        try {
            // Wywołaj API
            await firstValueFrom(this._notificationsApi.markAsRead(notificationId));

            const now = new Date().toISOString();

            // Update lokalnie w pamięci
            const notifications = this._notifications.value.map(n => {
                if (n._id === notificationId) {
                    return { ...n, isRead: true, readAt: now };
                }
                return n;
            });

            this._notifications.next(notifications);
            this.updateUnreadCount();

            // Update w Dexie
            if (this._db) {
                await this._db.notifications.update(notificationId, {
                    isRead: true,
                    readAt: now
                });
            }
        } catch (error) {
            console.error('[NotificationsService] Error marking as read:', error);
        }
    }

    /**
     * Oznacza wszystkie powiadomienia jako przeczytane
     */
    async markAllAsRead(): Promise<void> {
        try {
            // Wywołaj API
            await firstValueFrom(this._notificationsApi.markAllAsRead());

            const now = new Date().toISOString();

            // Update lokalnie w pamięci
            const notifications = this._notifications.value.map(n => ({
                ...n,
                isRead: true,
                readAt: n.readAt || now
            }));

            this._notifications.next(notifications);
            this._unreadCount.next(0);

            // Update w Dexie
            if (this._db) {
                const unreadIds = this._notifications.value
                    .filter(n => !n.isRead)
                    .map(n => n._id);

                for (const id of unreadIds) {
                    await this._db.notifications.update(id, {
                        isRead: true,
                        readAt: now
                    });
                }
            }
        } catch (error) {
            console.error('[NotificationsService] Error marking all as read:', error);
        }
    }

    /**
     * Usuwa powiadomienie (soft delete)
     */
    async deleteNotification(notificationId: string): Promise<void> {
        try {
            // Wywołaj API
            await firstValueFrom(this._notificationsApi.deleteNotification(notificationId));

            // Update lokalnie - usuń z listy
            const notifications = this._notifications.value.filter(n => n._id !== notificationId);
            this._notifications.next(notifications);
            this.updateUnreadCount();

            // Mark as deleted w Dexie (lub hard delete)
            if (this._db) {
                await this._db.notifications.update(notificationId, {
                    isDeleted: true
                });
            }
        } catch (error) {
            console.error('[NotificationsService] Error deleting notification:', error);
        }
    }

    /**
     * Usuwa wszystkie powiadomienia
     */
    async deleteAllNotifications(): Promise<void> {
        try {
            // Wywołaj API
            await firstValueFrom(this._notificationsApi.deleteAllNotifications());

            // Wyczyść lokalnie
            this._notifications.next([]);
            this._unreadCount.next(0);

            // Wyczyść Dexie
            if (this._db) {
                await this._db.notifications.clear();
            }
        } catch (error) {
            console.error('[NotificationsService] Error deleting all notifications:', error);
        }
    }

    // === Helpers ===

    /**
     * Pobiera domyślną ikonę dla typu powiadomienia
     */
    getIconForType(type: NotificationType): string {
        const icons: Record<NotificationType, string> = {
            [NotificationType.KSEF_EXPENSE_INVOICE]: 'tabler_outline:file-invoice',
            [NotificationType.KSEF_INCOME_INVOICE]: 'tabler_outline:file-invoice',
            [NotificationType.KSEF_SYNC_ERROR]: 'tabler_outline:alert-triangle',
            [NotificationType.KSEF_NEW_INVOICES_BATCH]: 'tabler_outline:file-invoice',
            [NotificationType.PAYMENT_DUE_SOON]: 'tabler_outline:clock',
            [NotificationType.PAYMENT_OVERDUE]: 'tabler_outline:alert-circle',
            [NotificationType.PAYMENT_RECEIVED]: 'tabler_outline:check',
            [NotificationType.SYSTEM_INFO]: 'tabler_outline:info-circle',
            [NotificationType.SYSTEM_WARNING]: 'tabler_outline:alert-triangle',
            [NotificationType.SYSTEM_ERROR]: 'tabler_outline:alert-octagon',
            [NotificationType.SUBSCRIPTION_EXPIRING]: 'tabler_outline:calendar-event',
            [NotificationType.SUBSCRIPTION_EXPIRED]: 'tabler_outline:calendar-off',
            [NotificationType.SUBSCRIPTION_RENEWED]: 'tabler_outline:calendar-check',
            [NotificationType.DEFAULT]: 'tabler_outline:bell'
        };

        return icons[type] || icons[NotificationType.DEFAULT];
    }

    /**
     * Pobiera klasę koloru dla priorytetu
     */
    getColorClassForPriority(priority: NotificationPriority): string {
        const colors: Record<NotificationPriority, string> = {
            [NotificationPriority.LOW]: 'text-gray-500',
            [NotificationPriority.NORMAL]: 'text-blue-500',
            [NotificationPriority.HIGH]: 'text-orange-500',
            [NotificationPriority.CRITICAL]: 'text-red-500'
        };

        return colors[priority] || colors[NotificationPriority.NORMAL];
    }

    /**
     * Pobiera klasę tła dla priorytetu (dla nieprzeczytanych)
     */
    getBgClassForPriority(priority: NotificationPriority): string {
        const colors: Record<NotificationPriority, string> = {
            [NotificationPriority.LOW]: 'bg-gray-100 dark:bg-gray-700',
            [NotificationPriority.NORMAL]: 'bg-blue-100 dark:bg-blue-900/30',
            [NotificationPriority.HIGH]: 'bg-orange-100 dark:bg-orange-900/30',
            [NotificationPriority.CRITICAL]: 'bg-red-100 dark:bg-red-900/30'
        };

        return colors[priority] || colors[NotificationPriority.NORMAL];
    }

    // === Private ===

    private updateUnreadCount(): void {
        const unread = this._notifications.value.filter(n => !n.isRead).length;
        this._unreadCount.next(unread);
    }
}
